import {
  loadFolderActivityLog,
  type FolderActivityAction,
  type FolderActivityEntry,
} from '../crypto/sharedFolderActivity';
import { applyI18n, t } from '../i18n';
import { bindLocaleRefresh } from '../i18n/bindLocaleRefresh';
import { enhanceDataTableCards } from './dataTableCards';

function actionLabel(action: FolderActivityAction): string {
  switch (action) {
    case 'upload':
      return t('files.owner.activityActionUpload');
    case 'create_directory':
      return t('files.owner.activityActionCreateDirectory');
    case 'rename':
      return t('files.owner.activityActionRename');
    case 'move':
      return t('files.owner.activityActionMove');
    case 'delete':
      return t('files.owner.activityActionDelete');
    default:
      return action;
  }
}

function describeEntry(entry: FolderActivityEntry): string {
  if (entry.fromPath && entry.toPath) {
    return `${entry.fromPath} → ${entry.toPath}`;
  }
  return entry.path ?? entry.toPath ?? entry.fromPath ?? '—';
}

export interface SharedFolderActivityPanelOptions {
  folderId: string;
  folderKey: Uint8Array;
}

export async function renderSharedFolderActivityPanel(
  host: HTMLElement,
  input: SharedFolderActivityPanelOptions,
): Promise<() => void> {
  host.innerHTML = `<p class="muted" data-i18n="status.loading"></p>`;
  applyI18n(host);

  let entries: FolderActivityEntry[] = [];
  try {
    entries = await loadFolderActivityLog(input.folderId, input.folderKey);
  } catch (cause) {
    console.error('Load folder activity failed', cause);
    host.innerHTML = `<p class="error-text" data-i18n="status.error"></p>`;
    applyI18n(host);
    return () => undefined;
  }

  const panel = document.createElement('div');
  panel.className = 'files-activity-panel';
  panel.innerHTML = `
    <p class="muted" data-i18n="files.owner.activitySubtitle"></p>
    <div class="files-table-wrap">
      <table class="data-table files-access-table">
        <thead>
          <tr>
            <th data-i18n="files.owner.activityWhen"></th>
            <th data-i18n="files.owner.activityWho"></th>
            <th data-i18n="files.owner.activityAction"></th>
            <th data-i18n="files.owner.activityWhat"></th>
          </tr>
        </thead>
        <tbody id="files-activity-body"></tbody>
      </table>
    </div>
    <p class="muted" id="files-activity-empty" hidden data-i18n="files.owner.activityEmpty"></p>
  `;

  host.replaceChildren(panel);
  applyI18n(panel);

  const tbody = panel.querySelector('#files-activity-body') as HTMLTableSectionElement;
  const emptyEl = panel.querySelector('#files-activity-empty') as HTMLParagraphElement;

  const renderRows = () => {
    tbody.replaceChildren();
    emptyEl.hidden = entries.length > 0;
    for (const entry of entries) {
      const row = document.createElement('tr');

      const whenCell = document.createElement('td');
      whenCell.textContent = new Date(entry.ts).toLocaleString();

      const whoCell = document.createElement('td');
      whoCell.textContent = entry.actor.displayName || entry.actor.id;

      const actionCell = document.createElement('td');
      actionCell.textContent = actionLabel(entry.action);

      const whatCell = document.createElement('td');
      whatCell.textContent = describeEntry(entry);
      whatCell.title = describeEntry(entry);

      row.append(whenCell, whoCell, actionCell, whatCell);
      tbody.append(row);
    }
    const table = panel.querySelector('table.data-table') as HTMLTableElement | null;
    if (table) {
      enhanceDataTableCards(table);
    }
  };

  renderRows();
  return bindLocaleRefresh(() => {
    applyI18n(panel);
    renderRows();
  });
}
